import { CURRENCY_OPTIONS } from '@/common/constant';
import type { Currency, Link } from '@/common/db-typebox';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Select, Space, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTreatyClient } from '../../lib/client';
import { useAuth } from '../../stores/AuthContext';

interface UserFormValues {
  name: string;
  email: string;
  phone?: string | null;
  address: {
    street?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    postalCode?: string | null;
  };
  links?: Link[];
  currency: Currency;
  system_prompt?: string | null;
}

export const EditProfilePage = observer(() => {
  const { user, isLoading, refetchUser } = useAuth();
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);
  const navigate = useNavigate();
  const client = useTreatyClient();
  const [userForm] = Form.useForm<UserFormValues>();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/');
    }
    if (user) {
      userForm.setFieldsValue({
        name: user.profile?.name,
        email: user.email,
        phone: user.profile?.phone,
        address: {
          street: user.profile?.street,
          city: user.profile?.city,
          state: user.profile?.state,
          country: user.profile?.country,
          postalCode: user.profile?.postal_code,
        },
        links: user.profile?.links || [],
        currency: user.profile?.currency || 'USD',
        system_prompt: user.profile?.system_prompt,
      });
    }
  }, [user, isLoading, navigate, userForm]);

  const handleUserSubmit = async (values: UserFormValues) => {
    try {
      setIsSubmittingUser(true);
      const response = await client.api.users.update.put({
        ...values,
        street: values.address.street,
        city: values.address.city,
        state: values.address.state,
        country: values.address.country,
        postal_code: values.address.postalCode,
      });

      if (response.error) {
        throw new Error(response.error.value.message);
      }

      await refetchUser();
      message.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      message.error('Failed to update profile');
    } finally {
      setIsSubmittingUser(false);
    }
  };

  if (isLoading || !user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container mx-auto p-8 space-y-8">
      <Form
        form={userForm}
        onFinish={handleUserSubmit}
        layout="vertical"
        requiredMark={false}
      >
        <Card title="Edit Profile">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label="Full Name"
              name="name"
              rules={[
                { required: true, message: 'Please input your full name' },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please input your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input disabled />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phone"
              rules={[
                {
                  pattern: /^[+]?[\d\s-]+$/,
                  message: 'Please enter a valid phone number',
                },
              ]}
            >
              <Input placeholder="Optional" />
            </Form.Item>

            <Form.Item
              label="Currency"
              name="currency"
              rules={[
                { required: true, message: 'Please select display currency' },
              ]}
              tooltip="Choose your preferred currency format for price display across the site"
            >
              <Select options={CURRENCY_OPTIONS} />
            </Form.Item>
          </div>

          <Card title="Business Address" className="mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Street Address"
                name={['address', 'street']}
                rules={[
                  {
                    required: true,
                    message: 'Please input your street address',
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="City"
                name={['address', 'city']}
                rules={[{ required: true, message: 'Please input your city' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="State/Province"
                name={['address', 'state']}
                rules={[
                  {
                    required: true,
                    message: 'Please input your state/province',
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Country"
                name={['address', 'country']}
                rules={[
                  { required: true, message: 'Please input your country' },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Postal Code"
                name={['address', 'postalCode']}
                rules={[
                  { required: true, message: 'Please input your postal code' },
                ]}
              >
                <Input />
              </Form.Item>
            </div>
          </Card>

          <Form.Item label="Links" hidden>
            <Form.List name="links">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="flex items-start gap-4 mb-2">
                      <Form.Item
                        {...restField}
                        name={[name, 'title']}
                        className="mb-0 flex-1"
                        rules={[
                          { required: true, message: 'Title is required' },
                        ]}
                      >
                        <Input placeholder="Title (e.g. Twitter, Portfolio)" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'url']}
                        className="mb-0 flex-1"
                        rules={[
                          { required: true, message: 'URL is required' },
                          {
                            type: 'url',
                            message: 'Please enter a valid URL',
                          },
                        ]}
                      >
                        <Input placeholder="URL (e.g. https://sommly.ai)" />
                      </Form.Item>
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                        className="min-w-10"
                        danger
                      />
                    </div>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Link
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item
            label="Restaurant preference"
            name="system_prompt"
            rules={[{ required: false }]}
            help="Custom system prompt for AI interactions. This will be used as a default instruction for all your AI conversations."
          >
            <Input.TextArea
              rows={6}
              placeholder="Enter your custom system prompt for AI interactions"
              className="font-mono text-sm"
            />
          </Form.Item>
          <Form.Item className="flex justify-end mb-0">
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmittingUser}
              >
                Save Profile
              </Button>
            </Space>
          </Form.Item>
        </Card>
      </Form>
    </div>
  );
});
